'use client'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { CheckCircle2, Circle } from 'lucide-react'

interface ADAMQuestionnaireProps {
  responses: Record<string, boolean>
  onChange: (responses: Record<string, boolean>) => void
}

const questions = [
  { id: 'q1_decreased_libido', text: 'Do you have a decrease in libido (sex drive)?' },
  { id: 'q2_lack_of_energy', text: 'Do you have a lack of energy?' },
  { id: 'q3_decreased_strength', text: 'Do you have a decrease in strength and/or endurance?' },
  { id: 'q4_lost_height', text: 'Have you lost height?' },
  { id: 'q5_decreased_enjoyment', text: 'Have you noticed a decreased enjoyment of life?' },
  { id: 'q6_sad_or_grumpy', text: 'Are you sad and/or grumpy?' },
  { id: 'q7_weaker_erections', text: 'Are your erections less strong?' },
  { id: 'q8_sports_performance_decline', text: 'Have you noticed a recent deterioration in your ability to play sports?' },
  { id: 'q9_falling_asleep_after_dinner', text: 'Are you falling asleep after dinner?' },
  { id: 'q10_work_performance_decline', text: 'Has there been a recent deterioration in your work performance?' },
]

export default function QuestionnaireADAM({ responses, onChange }: ADAMQuestionnaireProps) {
  const handleAnswer = (questionId: string, value: boolean) => {
    onChange({
      ...responses,
      [questionId]: value
    })
  }

  // Calculate score
  const yesCount = Object.values(responses).filter(val => val === true).length
  const allAnswered = questions.every(q => responses[q.id] !== undefined)
  const clinicallySignificant = yesCount >= 4

  return (
    <Card className="border-[#2FFFC7]/30 bg-[#2FFFC7]/5">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          ADAM Questionnaire
          {allAnswered && (
            <Badge 
              variant={clinicallySignificant ? 'default' : 'outline'}
              className={clinicallySignificant 
                ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' 
                : 'bg-[#2FFFC7]/20 text-[#2FFFC7] border-[#2FFFC7]/30'
              }
            >
              Score: {yesCount} / 10
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="text-white/60">
          Androgen Deficiency in Aging Males - Answer yes or no to each question
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {questions.map((question, index) => (
          <div key={question.id} className="bg-white/5 border border-white/10 rounded-lg p-4">
            <div className="font-medium text-sm mb-3 text-white">
              {index + 1}. {question.text}
            </div>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => handleAnswer(question.id, true)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 rounded-lg transition-all ${
                  responses[question.id] === true
                    ? 'border-[#2FFFC7] bg-[#2FFFC7]/10 text-[#2FFFC7]'
                    : 'border-white/20 hover:border-[#2FFFC7]/50 text-white/70'
                }`}
              >
                {responses[question.id] === true ? (
                  <CheckCircle2 className="h-5 w-5 text-[#2FFFC7]" />
                ) : (
                  <Circle className="h-5 w-5 text-white/40" />
                )}
                <span className="font-medium">Yes</span>
              </button>
              <button
                type="button"
                onClick={() => handleAnswer(question.id, false)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 border-2 rounded-lg transition-all ${
                  responses[question.id] === false
                    ? 'border-white/50 bg-white/10 text-white'
                    : 'border-white/20 hover:border-white/40 text-white/70'
                }`}
              >
                {responses[question.id] === false ? (
                  <CheckCircle2 className="h-5 w-5 text-white" />
                ) : (
                  <Circle className="h-5 w-5 text-white/40" />
                )}
                <span className="font-medium">No</span>
              </button>
            </div>
          </div>
        ))}

        {/* Score Interpretation */}
        {allAnswered && (
          <div className={`mt-6 p-4 rounded-lg border-2 ${
            clinicallySignificant 
              ? 'bg-amber-500/10 border-amber-500/30' 
              : 'bg-[#2FFFC7]/10 border-[#2FFFC7]/30'
          }`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-1">
                {clinicallySignificant ? (
                  <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold">
                    !
                  </div>
                ) : (
                  <CheckCircle2 className="h-8 w-8 text-[#2FFFC7]" />
                )}
              </div>
              <div className="flex-1">
                <div className={`font-semibold mb-1 ${
                  clinicallySignificant ? 'text-amber-400' : 'text-[#2FFFC7]'
                }`}>
                  {clinicallySignificant 
                    ? 'Clinically Significant Score' 
                    : 'Low Probability Score'
                  }
                </div>
                <div className={`text-sm ${
                  clinicallySignificant ? 'text-amber-300/80' : 'text-[#2FFFC7]/80'
                }`}>
                  {clinicallySignificant
                    ? 'A score of 4 or more "yes" answers suggests clinically relevant low testosterone symptoms. Your provider will review this with your lab work and medical history.'
                    : 'A score below 4 suggests a lower probability of hypogonadism, though your provider will still review all symptoms and lab values holistically.'
                  }
                </div>
              </div>
            </div>
          </div>
        )}

        {!allAnswered && (
          <div className="text-sm text-white/50 text-center py-2">
            Please answer all questions to see your score
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export { questions as adamQuestions }
